import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import React, { memo, useEffect, useState } from 'react';
import {
  calendarDateToMoment,
  formatSummaryDate,
  momentToCalendarDate,
} from '@/utils/date';
import { Chip, DatePickerCalendar, Popover } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import {
  DateRangeFilterSection,
  PricingLastInvoiceFilterValue,
  PricingRangeBound,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { Pressable, View } from 'react-native';
import { PricingFilterClearButton } from '@sales-portal/domain/pricing/components/pricing-filter-clear-button';
import { Typography } from '@usepepper/ui/atoms/typography';

type DateRangeSectionData = Omit<DateRangeFilterSection, 'getDate'>;
type RangeBoundKey = 'max' | 'min';

interface Props {
  customBound: PricingRangeBound;
  onClear: () => void;
  onSelectBucket: (value: string) => void;
  onSetCustomBound: (bound: RangeBoundKey, value: number | null) => void;
  section: DateRangeSectionData;
  selected: string[];
}

export const PricingFilterLastInvoiceSection: React.FC<Props> = memo(
  ({
    customBound,
    onClear,
    onSelectBucket,
    onSetCustomBound,
    section,
    selected,
  }) => {
    const [styles] = useStyles();
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [pendingRange, setPendingRange] = useState<Date[]>([]);

    const isCustomSelected = selected.includes(
      PricingLastInvoiceFilterValue.Custom,
    );
    const hasCustomRange = customBound.min !== null && customBound.max !== null;

    // Re-seed from the committed bound each time the popover opens, so a
    // cancelled edit (closed without pressing Save) leaves it untouched.
    useEffect(() => {
      if (!isPickerOpen) {
        return;
      }
      setPendingRange(
        hasCustomRange
          ? [
              momentToCalendarDate(moment(customBound.min as number).tz('UTC')),
              momentToCalendarDate(moment(customBound.max as number).tz('UTC')),
            ]
          : [],
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPickerOpen]);

    const handleAccept = () => {
      if (pendingRange.length !== 2) {
        return;
      }
      if (!isCustomSelected) {
        onSelectBucket(PricingLastInvoiceFilterValue.Custom);
      }
      // UTC-anchor the picked days so a shared link reads the same everywhere.
      onSetCustomBound(
        'min',
        calendarDateToMoment(pendingRange[0], 'UTC').valueOf(),
      );
      onSetCustomBound(
        'max',
        calendarDateToMoment(pendingRange[1], 'UTC').valueOf(),
      );
      setIsPickerOpen(false);
    };

    const handleClear = () => {
      onClear();
      setIsPickerOpen(false);
    };

    const customLabel = hasCustomRange
      ? `${formatSummaryDate(moment(customBound.min as number).tz('UTC'))} - ${formatSummaryDate(moment(customBound.max as number).tz('UTC'))}`
      : i18n.t('pricing.filters.last_invoice.custom');

    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Typography variant="label-md">{section.title}</Typography>
          {selected.length > 0 ? (
            <PricingFilterClearButton onPress={onClear} />
          ) : null}
        </View>
        <View style={styles.pillRow}>
          <Pressable
            accessibilityLabel={i18n.t('pricing.filters.last_invoice.never')}
            accessibilityRole="radio"
            accessibilityState={{
              checked: selected.includes(PricingLastInvoiceFilterValue.Never),
            }}
            onPress={() => onSelectBucket(PricingLastInvoiceFilterValue.Never)}
          >
            <Chip
              circular
              pressable
              selected={selected.includes(PricingLastInvoiceFilterValue.Never)}
              size="$md"
            >
              <Chip.Text>
                {i18n.t('pricing.filters.last_invoice.never')}
              </Chip.Text>
            </Chip>
          </Pressable>
          <Pressable
            accessibilityLabel={i18n.t('pricing.filters.last_invoice.recent')}
            accessibilityRole="radio"
            accessibilityState={{
              checked: selected.includes(PricingLastInvoiceFilterValue.Recent),
            }}
            onPress={() => onSelectBucket(PricingLastInvoiceFilterValue.Recent)}
          >
            <Chip
              circular
              pressable
              selected={selected.includes(PricingLastInvoiceFilterValue.Recent)}
              size="$md"
            >
              <Chip.Text>
                {i18n.t('pricing.filters.last_invoice.recent')}
              </Chip.Text>
            </Chip>
          </Pressable>
          <Popover
            allowFlip
            onOpenChange={setIsPickerOpen}
            open={isPickerOpen}
            placement="bottom-start"
          >
            <Popover.Anchor asChild>
              <Pressable
                accessibilityLabel={customLabel}
                accessibilityRole="radio"
                accessibilityState={{
                  checked: isCustomSelected,
                  expanded: isPickerOpen,
                }}
                onPress={() => setIsPickerOpen((prev) => !prev)}
              >
                <Chip circular pressable selected={isCustomSelected} size="$md">
                  <Chip.Text>{customLabel}</Chip.Text>
                </Chip>
              </Pressable>
            </Popover.Anchor>
            <Popover.Content padding="$16">
              <DatePickerCalendar
                mode="range"
                onAccept={handleAccept}
                onChange={setPendingRange}
                onClear={handleClear}
                value={pendingRange}
              />
            </Popover.Content>
          </Popover>
        </View>
      </View>
    );
  },
);

const useStyles = createStyles((theme) => ({
  container: {
    gap: theme.sk.spacing.spaceSmall,
    paddingBottom: theme.sk.spacing.spaceLarge,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
